/**
 * What the stylized planet surface needs per tile beyond its colour: a random value, how much of a
 * pond's rim is shore at each corner, the shade under what stands on a tile, and which ground
 * blends at its corners. Pure, so it is tested without a renderer.
 */
import { KIND, type Grid } from '@bramvasterd/terrella-rules';

import { hash2 } from '@core/math';

/** A tile's own random value in 0..1, the same on every device. */
export const tileSeed = (tile: number): number => hash2(tile, 97);

/** The other two tiles at corner `k` of `tile` (`out` is reused); -1 for a pentagon's padding. */
export function cornerOthers(
  grid: Grid,
  tile: number,
  k: number,
  out: [number, number],
): [number, number] {
  out[0] = -1;
  out[1] = -1;
  const corner = grid.tileCorners[tile * 6 + k] ?? -1;
  if (corner < 0) return out;
  let n = 0;
  for (let j = 0; j < 3; j++) {
    const t = grid.cornerTiles[corner * 3 + j] ?? -1;
    if (t !== tile && n < 2) out[n++] = t;
  }
  return out;
}

/** Whether a tile is drawn: every tile, unless the view shows only some (the Tile Lab). */
export const isShown = (shown: Uint8Array | undefined, tile: number): boolean =>
  tile >= 0 && (shown === undefined || shown[tile] === 1);

const pair: [number, number] = [-1, -1];

/**
 * How much of a water tile's rim is shore at corner `k`: the share of the other two tiles there
 * that are not water. A tile that isn't drawn counts as shore, so a pond on its own has a beach.
 */
export function shoreAt(
  grid: Grid,
  kind: ArrayLike<number>,
  shown: Uint8Array | undefined,
  tile: number,
  k: number,
): number {
  cornerOthers(grid, tile, k, pair);
  let land = 0;
  if (!isShown(shown, pair[0]) || kind[pair[0]] !== KIND.WATER) land++;
  if (!isShown(shown, pair[1]) || kind[pair[1]] !== KIND.WATER) land++;
  return land / 2;
}

/** How light the middle of a tile stays under what stands on it: a tree shades it most. */
export function shadeUnder(kind: number): number {
  switch (kind) {
    case KIND.TREE:
    case KIND.FRUIT_TREE:
      return 0.62;
    case KIND.SAPLING:
    case KIND.FRUIT_SAPLING:
      return 0.86;
    default:
      return 1;
  }
}

/** Ground whose corners blend into a neighbour of another kind: not water or the hub. */
export function blendsAtCorners(kind: number): boolean {
  switch (kind) {
    case KIND.STONE:
    case KIND.SOIL:
    case KIND.GRASS:
    case KIND.SAPLING:
    case KIND.FRUIT_SAPLING:
    case KIND.TREE:
    case KIND.FRUIT_TREE:
    case KIND.ORE:
    case KIND.BASALT:
    case KIND.GOLD:
    case KIND.CORE:
      return true;
    default:
      return false;
  }
}
